import React, { useState } from 'react'
import { TaskBoardType } from '../../types/task-board'
import { TaskColumnCard } from './TaskColumnCard'
import { TaskCardPriority } from './TaskCardPriority'
import { TaskCardAssignee } from './TaskCardAssignee'
import { AddDependencyModal } from './AddDependencyModal'
import { PREDEFINED_STAGES, PREDEFINED_TASKS, PREDEFINED_SUBTASKS } from '../../common/task-stages'
import { TaskCardDueDate } from './TaskCardDueDate'

interface TaskBoardModalsProps {
  board: TaskBoardType
  showInviteModal: boolean
  showAddColumnModal: boolean
  showAddCardModal: string | null
  showCardModal: { columnId: string, cardId: string } | null
  showPriorityModal: { columnId: string, cardId: string } | null
  showAssigneeModal: { columnId: string, cardId: string } | null
  showDependencyModal: { columnId: string, cardId: string } | null
  showDueDateModal: { columnId: string, cardId: string } | null
  inviteEmail: string
  newColumnTitle: string
  newCard: { title: string, description: string, priority: number }
  isPredefineMode: boolean
  selectedStage: string
  selectedTask: string
  dependencies: any[]
  packageUsersNumber?: number
  onInviteModalClose: () => void
  onAddColumnModalClose: () => void
  onAddCardModalClose: () => void
  onCardModalClose: () => void
  onPriorityModalClose: () => void
  onAssigneeModalClose: () => void
  onDependencyModalClose: () => void
  onDueDateModalClose: () => void
  onInviteEmailChange: (email: string) => void
  onNewColumnTitleChange: (title: string) => void
  onNewCardChange: (card: { title: string, description: string, priority: number }) => void
  onPredefineModeChange: (mode: boolean) => void
  onSelectedStageChange: (stage: string) => void
  onSelectedTaskChange: (task: string) => void
  onInviteUser: () => void
  onRemoveMember: (memberId: string) => void
  onAddColumn: () => void
  onAddCard: (columnId: string) => void
  onAddPredefinedCard: (columnId: string, stageName: string, taskName: string, subtaskName?: string) => void
  onUpdateCardPriority: (cardId: string, priority: number) => void
  onUpdateCardDueDates: (cardId: string, dueDate: string | null, dueDateTo: string | null) => void
  onAssignUser: (cardId: string, userId: string | null) => void
  onAddDependency: (cardId: string, dependencyData: {
    dependencyType: string
    stageName: string
    customTaskName: string
  }) => void
  onUpdateDependency: (cardId: string, dependencyId: string, dependencyData: {
    dependencyType: string
    stageName: string
    customTaskName: string
  }) => void
  onRemoveDependency: (cardId: string, dependencyId: string) => void
  onAddComment: (cardId: string, commentContent: string) => Promise<void>
  onDeleteCard: (columnId: string, cardId: string) => void
  onUpdateCardDescription: (cardId: string, description: string) => Promise<void>
  onMoveCard: (cardId: string, sourceColumnId: string, destinationColumnId: string) => Promise<void>
  onChangeCardPosition: (cardId: string, columnId: string, direction: 'up' | 'down') => Promise<void>
  onUpdateNotifyOnComplete: (cardId: string, notifyOnComplete: boolean) => Promise<void>
  onDueDateClick: (columnId: string, cardId: string) => void
}

export const TaskBoardModals: React.FC<TaskBoardModalsProps> = ({
  board,
  showInviteModal,
  showAddColumnModal,
  showAddCardModal,
  showCardModal,
  showPriorityModal,
  showAssigneeModal,
  showDependencyModal,
  showDueDateModal,
  inviteEmail,
  newColumnTitle,
  newCard,
  isPredefineMode,
  selectedStage,
  selectedTask,
  dependencies,
  packageUsersNumber,
  onInviteModalClose,
  onAddColumnModalClose,
  onAddCardModalClose,
  onCardModalClose,
  onPriorityModalClose,
  onAssigneeModalClose,
  onDependencyModalClose,
  onDueDateModalClose,
  onInviteEmailChange,
  onNewColumnTitleChange,
  onNewCardChange,
  onPredefineModeChange,
  onSelectedStageChange,
  onSelectedTaskChange,
  onInviteUser,
  onRemoveMember,
  onAddColumn,
  onAddCard,
  onAddPredefinedCard,
  onUpdateCardPriority,
  onUpdateCardDueDates,
  onAssignUser,
  onAddDependency,
  onUpdateDependency,
  onRemoveDependency,
  onAddComment,
  onDeleteCard,
  onUpdateCardDescription,
  onMoveCard,
  onChangeCardPosition,
  onUpdateNotifyOnComplete,
  onDueDateClick
}) => {
  const [selectedSubtask, setSelectedSubtask] = useState('')

  const handleAssignUser = async (cardId: string, userId: string | null): Promise<void> => {
    await onAssignUser(cardId, userId)
  }

  const handleUpdateCardPriority = async (cardId: string, priority: number): Promise<void> => {
    await onUpdateCardPriority(cardId, priority)
  }

  const handleAddDependency = async (cardId: string, dependencyData: {
    dependencyType: string
    stageName: string
    customTaskName: string
  }): Promise<void> => {
    await onAddDependency(cardId, dependencyData)
  }

  const handleUpdateDependency = async (cardId: string, dependencyId: string, dependencyData: {
    dependencyType: string
    stageName: string
    customTaskName: string
  }): Promise<void> => {
    await onUpdateDependency(cardId, dependencyId, dependencyData)
  }

  const handleRemoveDependency = async (cardId: string, dependencyId: string): Promise<void> => {
    await onRemoveDependency(cardId, dependencyId)
  }

  const handleUpdateCardDueDates = async (cardId: string, dueDate: string | null, dueDateTo: string | null): Promise<void> => {
    await onUpdateCardDueDates(cardId, dueDate, dueDateTo)
  }

  return (
    <>
      {/* Modal do zapraszania użytkowników */}
      {showInviteModal && (
        <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Zaproś użytkownika</h5>
                <button type='button' className='close' onClick={onInviteModalClose}>
                  <span>&times;</span>
                </button>
              </div>
              <div className='modal-body'>
                {packageUsersNumber && (
                  <div className='mb-3'>
                    <div className='d-flex justify-content-between'>
                      <span>Aktualnie zaproszeni użytkownicy:</span>
                      <span className={board.members.length >= packageUsersNumber
                        ? 'text-danger font-weight-bold'
                        : ''}
                      >
                        {board.members.length} / {packageUsersNumber}
                      </span>
                    </div>
                    {board.members.length >= packageUsersNumber && (
                      <div className='alert alert-warning mt-2'>
                        Osiągnięto limit użytkowników dostępny w Twoim pakiecie.
                        Rozważ aktualizację pakietu, aby zaprosić więcej osób.
                      </div>
                    )}
                  </div>
                )}

                {board.members.length > 0 && (
                  <div className='mb-4'>
                    <h6 className='mb-3'>Lista użytkowników:</h6>
                    <div className='list-group'>
                      {board.members.map((member, index) => (
                        <div key={index} className='list-group-item d-flex justify-content-between align-items-center'>
                          <div>
                            <span className='fw-bold'>{member.username || member.email}</span>
                            <br />
                            <small className='text-muted'>{member.email}</small>
                          </div>
                          {member.role !== 'owner' && (
                            <button
                              type='button'
                              className='btn btn-danger btn-sm'
                              onClick={() => onRemoveMember(member.id)}
                            >
                              <i className='fa fa-trash' />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className='form-group'>
                  <label>Adres email:</label>
                  <input
                    type='email'
                    className='form-control'
                    placeholder='Wprowadź adres email'
                    value={inviteEmail}
                    onChange={(e) => onInviteEmailChange(e.target.value)}
                    disabled={!!packageUsersNumber && board.members.length >= packageUsersNumber}
                  />
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className={`
                                      tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                    `}
                  onClick={onInviteModalClose}
                >
                  Anuluj
                </button>
                <button
                  type='button'
                  className={`
                                      tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                    `}
                  onClick={onInviteUser}
                  disabled={!inviteEmail.trim() || (!!packageUsersNumber && board.members.length >= packageUsersNumber)}
                >
                  Wyślij zaproszenie
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal do dodawania kolumny */}
      {showAddColumnModal && (
        <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Dodaj nową kolumnę</h5>
                <button type='button' className='close' onClick={onAddColumnModalClose}>
                  <span>&times;</span>
                </button>
              </div>
              <div className='modal-body'>
                <div className='form-group'>
                  <label>Nazwa kolumny:</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Wprowadź nazwę kolumny'
                    value={newColumnTitle}
                    onChange={(e) => onNewColumnTitleChange(e.target.value)}
                  />
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className={`
                                      tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                    `}
                  onClick={onAddColumnModalClose}
                >
                  Anuluj
                </button>
                <button
                  type='button'
                  className={`
                                      tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                    `}
                  onClick={onAddColumn}
                  disabled={!newColumnTitle.trim()}
                >
                  Dodaj kolumnę
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal do dodawania karty */}
      {showAddCardModal && (
        <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div
            className='modal-dialog modal-dialog-scrollable' style={{
              maxHeight: '90vh',
              margin: '1.75rem auto'
            }}
          >
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Dodaj nowe zadanie</h5>
                <button type='button' className='close' onClick={onAddCardModalClose}>
                  <span>&times;</span>
                </button>
              </div>
              <div className='modal-body' style={{ maxHeight: 'calc(90vh - 210px)', overflowY: 'auto' }}>
                <div className='d-flex justify-content-center mb-4'>
                  <div className='task-modal-tabs'>
                    <button
                      type='button'
                      className={`task-modal-tab${!isPredefineMode ? ' active' : ''}`}
                      onClick={() => {
                        onPredefineModeChange(false)
                        onSelectedStageChange('')
                        onSelectedTaskChange('')
                      }}
                    >
                      <i className='fa fa-pencil me-2' />
                      Własne zadanie
                    </button>
                    <button
                      type='button'
                      className={`task-modal-tab${isPredefineMode ? ' active' : ''}`}
                      onClick={() => onPredefineModeChange(true)}
                    >
                      <i className='fa fa-list me-2' />
                      Z szablonu
                    </button>
                  </div>
                </div>

                {isPredefineMode
                  ? (
                    <div>
                      <div className='form-group mb-4'>
                        <label className='form-label fw-bold'>Wybierz kategorie:</label>
                        <select
                          className='form-select form-control'
                          value={selectedStage}
                          onChange={(e) => {
                            onSelectedStageChange(e.target.value)
                            onSelectedTaskChange('')
                            setSelectedSubtask('')
                          }}
                          style={{
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #ced4da',
                            fontSize: '1rem'
                          }}
                        >
                          <option value=''>Wybierz kategorię...</option>
                          {PREDEFINED_STAGES.map((stage: string) => (
                            <option key={stage} value={stage}>{stage}</option>
                          ))}
                        </select>
                      </div>

                      {selectedStage && (
                        <div className='form-group mb-4'>
                          <label className='form-label fw-bold'>Wybierz podkategorię:</label>
                          <select
                            className='form-select form-control'
                            value={selectedTask}
                            onChange={(e) => {
                              onSelectedTaskChange(e.target.value)
                              setSelectedSubtask('')
                            }}
                            style={{
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #ced4da',
                              fontSize: '1rem'
                            }}
                          >
                            <option value=''>Wybierz podkategorię...</option>
                            {PREDEFINED_TASKS[selectedStage] && PREDEFINED_TASKS[selectedStage].map((task: string) => (
                              <option key={task} value={task}>{task}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {selectedStage && selectedTask && (
                        <div className='form-group mb-4'>
                          <label className='form-label fw-bold'>Wybierz etap:</label>
                          <select
                            className='form-select form-control'
                            value={selectedSubtask}
                            onChange={(e) => setSelectedSubtask(e.target.value)}
                            style={{
                              padding: '10px',
                              borderRadius: '6px',
                              border: '1px solid #ced4da',
                              fontSize: '1rem'
                            }}
                          >
                            <option value=''>Wybierz etap...</option>
                            {PREDEFINED_SUBTASKS[selectedStage] && PREDEFINED_SUBTASKS[selectedStage][selectedTask] &&
                            PREDEFINED_SUBTASKS[selectedStage][selectedTask].map((subtask: string) => (
                              <option key={subtask} value={subtask}>{subtask}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    )
                  : (
                    <>
                      <div className='form-group mb-20'>
                        <label className='form-label fw-bold'>Tytuł zadania:</label>
                        <input
                          type='text'
                          className='form-control'
                          placeholder='Wprowadź tytuł zadania'
                          value={newCard.title}
                          onChange={(e) => onNewCardChange({ ...newCard, title: e.target.value })}
                          style={{
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #ced4da',
                            fontSize: '1rem'
                          }}
                        />
                      </div>
                      <div className='form-group mb-20'>
                        <label className='form-label fw-bold'>Opis zadania:</label>
                        <textarea
                          className='form-control'
                          rows={4}
                          placeholder='Wprowadź opis zadania'
                          value={newCard.description}
                          onChange={(e) => onNewCardChange({ ...newCard, description: e.target.value })}
                          style={{
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #ced4da',
                            fontSize: '1rem'
                          }}
                        />
                      </div>
                      <div className='form-group'>
                        <label className='form-label fw-bold'>Priorytet:</label>
                        <select
                          className='form-control'
                          value={newCard.priority || 0}
                          onChange={(e) => onNewCardChange({ ...newCard, priority: parseInt(e.target.value) })}
                          style={{
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #ced4da',
                            fontSize: '1rem'
                          }}
                        >
                          <option value={0}>Normalny</option>
                          <option value={1}>Niski</option>
                          <option value={2}>Średni</option>
                          <option value={3}>Wysoki</option>
                          <option value={4}>Krytyczny</option>
                        </select>
                      </div>
                    </>
                    )}
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className={`
                                      tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                    `}
                  onClick={onAddCardModalClose}
                >
                  Anuluj
                </button>

                <button
                  type='button'
                  className={`
                                      tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                    `}
                  onClick={async () => {
                    if (isPredefineMode) {
                      if (selectedStage && selectedTask) {
                        let taskTitle = selectedTask
                        if (selectedSubtask) {
                          taskTitle = `${selectedTask} / ${selectedSubtask}`
                        }
                        await onAddPredefinedCard(showAddCardModal, selectedStage, taskTitle)
                      }
                    } else {
                      await onAddCard(showAddCardModal)
                    }
                  }}
                  disabled={isPredefineMode ? !selectedStage || !selectedTask : !newCard.title.trim()}
                >
                  Dodaj zadanie
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal ze szczegółami karty */}
      {(showCardModal != null) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: window.innerWidth < 768 ? '20px 10px' : '40px 20px',
          overflow: 'auto'
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 5px 15px rgba(0,0,0,0.5)',
            width: '100%',
            maxWidth: '900px',
            position: 'relative',
            margin: '0 auto'
          }}
          >
            {board?.columns.map(column =>
              column.id === showCardModal.columnId &&
              column.cards
                .filter(card => card.id === showCardModal.cardId)
                .map(card => (
                  <TaskColumnCard
                    key={card.id}
                    card={card}
                    column={column}
                    board={board}
                    showCardModal={showCardModal}
                    setShowCardModal={onCardModalClose}
                    handleAddComment={onAddComment}
                    handleDeleteCard={onDeleteCard}
                    handleAssignUser={handleAssignUser}
                    handleUpdateCardDescription={onUpdateCardDescription}
                    handleMoveCard={onMoveCard}
                    handleChangeCardPosition={onChangeCardPosition}
                    handleUpdateCardPriority={handleUpdateCardPriority}
                    handleUpdateCardDueDates={handleUpdateCardDueDates}
                    handleAddDependency={handleAddDependency}
                    handleUpdateDependency={handleUpdateDependency}
                    handleRemoveDependency={handleRemoveDependency}
                    handleUpdateNotifyOnComplete={onUpdateNotifyOnComplete}
                  />
                ))
            )}
          </div>
        </div>
      )}

      {/* Modal do zmiany priorytetu */}
      {(showPriorityModal != null) && (
        <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Zmień priorytet zadania</h5>
                <button type='button' className='close' onClick={onPriorityModalClose}>
                  <span>&times;</span>
                </button>
              </div>
              <div className='modal-body'>
                {board?.columns.map(column =>
                  column.id === showPriorityModal.columnId &&
                  column.cards
                    .filter(card => card.id === showPriorityModal.cardId)
                    .map(card => (
                      <TaskCardPriority
                        key={card.id}
                        card={card}
                        onUpdatePriority={async (priority: number) => {
                          await onUpdateCardPriority(card.id, priority)
                          onPriorityModalClose()
                        }}
                      />
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal do przypisywania użytkownika */}
      {(showAssigneeModal != null) && (
        <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Przypisz użytkownika</h5>
                <button type='button' className='close' onClick={onAssigneeModalClose}>
                  <span>&times;</span>
                </button>
              </div>
              <div className='modal-body'>
                {board?.columns.map(column =>
                  column.id === showAssigneeModal.columnId &&
                  column.cards
                    .filter(card => card.id === showAssigneeModal.cardId)
                    .map(card => (
                      <TaskCardAssignee
                        key={card.id}
                        card={card}
                        board={board}
                        onAssignUser={async (userId) => {
                          await onAssignUser(card.id, userId)
                          onAssigneeModalClose()
                        }}
                      />
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal do powiązań */}
      {(showDependencyModal != null) && (
        <AddDependencyModal
          onClose={onDependencyModalClose}
          onAddDependency={async (dependencyData) => {
            if (showDependencyModal) {
              await onAddDependency(showDependencyModal.cardId, dependencyData)
            }
          }}
          onUpdateDependency={async (dependencyId, dependencyData) => {
            if (showDependencyModal) {
              await onUpdateDependency(showDependencyModal.cardId, dependencyId, dependencyData)
            }
          }}
          onRemoveDependency={async (dependencyId) => {
            if (showDependencyModal) {
              await onRemoveDependency(showDependencyModal.cardId, dependencyId)
            }
          }}
          existingDependencies={dependencies}
        />
      )}

      {/* Modal do ustawiania terminu */}
      {(showDueDateModal != null) && (
        <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Ustaw termin wykonania</h5>
                <button type='button' className='close' onClick={onDueDateModalClose}>
                  <span>&times;</span>
                </button>
              </div>
              <div className='modal-body'>
                {board?.columns.map(column =>
                  column.id === showDueDateModal.columnId &&
                  column.cards
                    .filter(card => card.id === showDueDateModal.cardId)
                    .map(card => (
                      <TaskCardDueDate
                        key={card.id}
                        card={card}
                        onUpdateDueDates={async (dueDate, dueDateTo) => {
                          await onUpdateCardDueDates(card.id, dueDate, dueDateTo)
                          onDueDateModalClose()
                        }}
                      />
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
